import { IconButton, useDisclosure, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import { FiShare2 } from 'react-icons/fi';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { useAudioContext } from '../context/AudioContext';
import { LoadedFile } from '../services/FileService';

const ShareModal = lazy(() => import('./ShareModal'));

interface ShareButtonProps {
  loadedFiles?: LoadedFile[];
}

const ShareButton = ({ loadedFiles = [] }: ShareButtonProps) => {
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isTooltipOpen, onOpen: onTooltipOpen, onClose: onTooltipClose } = useDisclosure();
  const { audioElements } = useAudioContext();
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    const handleDocumentClick = () => {
      if (isTooltipOpen) {
        onTooltipClose();
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [isTooltipOpen, onTooltipClose]);

  // Check if ALL files are shareable (have shareableUrl)
  // If any file is local (not shareable), the setup cannot be shared
  // Use useMemo to ensure this recalculates when loadedFiles changes
  const hasShareableFiles = useMemo(() => {
    if (loadedFiles.length === 0) return false;
    
    return loadedFiles.every(file => {
      // Files must have a file object
      if (!file.file) return false;
      // Files with a non-empty shareableUrl are shareable
      // Check for both undefined and empty string cases
      const shareableUrl = file.shareableUrl;
      const hasShareableUrl = shareableUrl !== undefined && 
                             shareableUrl !== null &&
                             shareableUrl !== '' && 
                             shareableUrl.length > 0;
      return hasShareableUrl;
    });
  }, [loadedFiles]);

  const isDisabled = !hasShareableFiles;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDisabled && isMobile) {
      onTooltipOpen();
    } else if (!isDisabled) {
      onModalOpen();
    }
  };

  return (
    <>
      <Tooltip 
        label={isDisabled ? "Import Dropbox files to enable sharing" : "Share your setup, including track notes."} 
        placement="top"
        isOpen={isMobile ? isTooltipOpen : undefined}
        onClose={onTooltipClose}
      >
        <IconButton
          data-testid="share-button"
          aria-label="Share Mix"
          icon={<FiShare2 size={36} />}
          onClick={handleClick}
          isDisabled={false}
          variant="ghost"
          size="lg"
          width="72px"
          height="72px"
          colorScheme="blue"
          opacity={isDisabled ? 0.5 : 1}
          _hover={{ 
            color: isDisabled ? "inherit" : "accent.400",
            transform: 'scale(1.05)',
            borderColor: 'transparent'
          }}
          _active={{ 
            color: "accent.500",
            transform: 'scale(0.95)',
            borderColor: 'transparent'
          }}
          _focus={{ boxShadow: "none" }}
          _focusVisible={{ boxShadow: "none" }}
        />
      </Tooltip>
      <Suspense fallback={null}>
        {isModalOpen && <ShareModal isOpen={isModalOpen} onClose={onModalClose} loadedFiles={loadedFiles} />}
      </Suspense>
    </>
  );
};

export default ShareButton; 